// File: CarAdminFormNew - dynamic model editor (steps / screens / rules)
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Boxes, Car, Check, ChevronDown, ChevronRight, ChevronUp, Copy, Download, Eye, EyeOff, FileArchive,
  GripVertical, Layers, Loader2, Mountain, Pencil, Plus, Rocket, RotateCcw, Save, Search, Sparkles, ToggleRight, Trash2, Type, Upload, X,
} from 'lucide-react';
import { uploadImage, createCar, updateCar, fetchAllCars } from '../services/api.js';
import CarWizardConfiguratorNew from './CarWizardConfiguratorNew';
import { isLegacyCar, isRenderableImage, normalizeCar, slug } from './carWizard/carModel';
import { collectRenderEntries, downloadRendersZip } from './carWizard/downloadRenders';
import { HistoryPanel, ModelsManager, PublishDialog, UNASSIGNED, modelNameOf } from './carWizard/AdminModels';
import { IS_PREVIEW } from '../config.js';
import { exampleCar } from './carWizard/exampleCar';
import { VIEW_KEYS } from './carWizard/carWizardTypes';
import './carWizard/admin.css';
import type {
  CarConfig,
  Option,
  Overlay,
  OverlayPosition,
  PreferredView,
  Rule,
  RuleEffect,
  RuleEffectType,
  Scenery,
  Screen,
  Step,
  SubOption,
  ViewImages,
  ViewKey,
} from './carWizard/carWizardTypes';

const VIEW_LABELS = [
  'View 1 (Exterior 3/4 Front)',
  'View 2 (Exterior 3/4 Rear)',
  'View 3 (Wheel Closeup)',
  'View 4 (Interior 3/4 Front)',
  'View 5 (Top View)',
  'View 6 (Front View)',
];

const EFFECT_TYPES: RuleEffectType[] = [
  'disableOption',
  'hideOption',
  'disableStep',
  'hideStep',
  'hideScreen',
  'forceOption',
  'forceSubOption',
];

let _uid = 0;
const uid = (prefix: string) => `${prefix}-${++_uid}`;

// Move item from `from` to `to`, returning a new array.
const reorder = <T,>(arr: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// A view is "resolved" when it has an http image OR an explicit Not-Apply marker
// ("x" / "not visible in this view"). Empty/undefined = unresolved.
const isViewResolved = (v?: string) => typeof v === 'string' && v.trim().length > 0;

const emptyCar = (): CarConfig => ({
  id: '',
  createdAt: '',
  carName: '',
  basePrice: 0,
  baseRenderOptionId: '',
  steps: [],
  screens: [],
  rules: [],
});

/** Unique option id within a step. */
const genOptionId = (step: Step): string => {
  const prefix = (slug(step.stepName || step.stepId).replace(/-/g, '').slice(0, 3) || 'opt').toUpperCase();
  const existing = new Set(step.options.map((o) => o.optionId));
  let n = step.options.length + 1;
  let id = `${prefix}${n}`;
  while (existing.has(id)) id = `${prefix}${++n}`;
  return id;
};

/** Unique slug id from a label, avoiding `taken`. */
const uniqueSlug = (label: string, taken: string[], fallback: string): string => {
  const base = slug(label) === 'step' ? fallback : slug(label);
  let id = base;
  let i = 2;
  while (taken.includes(id)) id = `${base}-${i++}`;
  return id;
};

const genSubOptionId = (option: Option): string => {
  const base = option.optionId || 'OPT';
  const existing = new Set((option.subOptions || []).map((s) => s.optionId));
  let n = (option.subOptions?.length || 0) + 1;
  let id = `${base}${n}`;
  while (existing.has(id)) id = `${base}${++n}`;
  return id;
};

const CarAdminFormNew = () => {
  const [car, setCar] = useState<CarConfig>(emptyCar);
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [debouncedCar, setDebouncedCar] = useState<CarConfig>(car);

  // Car picker (edit existing).
  const [showPicker, setShowPicker] = useState(false);
  const [cars, setCars] = useState<CarConfig[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  // Confirmation modal (replaces native confirm()).
  type ConfirmOpts = { confirmLabel?: string; hold?: boolean; danger?: boolean };
  const [confirmState, setConfirmState] = useState<({ message: string; onConfirm: () => void } & ConfirmOpts) | null>(null);
  const askConfirm = (message: string, onConfirm: () => void, opts: ConfirmOpts = {}) =>
    setConfirmState({ message, onConfirm, ...opts });

  // UI: which editor panel is active + which steps are expanded.
  const [activeTab, setActiveTab] = useState<'steps' | 'screens' | 'rules' | 'overlays' | 'sceneries' | 'history'>('steps');
  // Model manager, staging → prod publish, history refresh, unsaved-changes tracking.
  const [showModels, setShowModels] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [savedMarker, setSavedMarker] = useState(0);
  const [savedJson, setSavedJson] = useState<string | null>(null);
  // Save dialog (rename / save as copy) and legacy-migration notice.
  const [saveDialog, setSaveDialog] = useState<{ name: string } | null>(null);
  // "Renders ZIP" progress (null = idle).
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);
  const [loadedFromLegacy, setLoadedFromLegacy] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [dragStep, setDragStep] = useState<number | null>(null);
  const toggleStepExpanded = (stepId: string) =>
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });

  const isEditing = !!(car.id && car.createdAt);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCar(car), 400);
    return () => clearTimeout(t);
  }, [car]);

  const previewCarData = useMemo(
    () => normalizeCar({ ...debouncedCar, id: 'admin-preview', createdAt: 'admin-preview' }),
    [debouncedCar]
  );

  /* ------------------------------------------------------------------ *
   * Validation (mirrors backend invariants, doc §1.3).
   * ------------------------------------------------------------------ */
  const validationErrors = useMemo((): string[] => {
    const out: string[] = [];
    const stepIds = car.steps.map((s) => s.stepId);
    const dupStep = stepIds.find((id, i) => stepIds.indexOf(id) !== i);
    if (dupStep) out.push(`Duplicate stepId "${dupStep}"`);

    // Each step in exactly one screen.
    const screenCount = new Map<string, number>();
    car.screens.forEach((sc) => sc.stepIds.forEach((id) => screenCount.set(id, (screenCount.get(id) || 0) + 1)));
    car.steps.forEach((s) => {
      const c = screenCount.get(s.stepId) || 0;
      if (c === 0) out.push(`Step "${s.stepName || s.stepId}" is not in any screen`);
      if (c > 1) out.push(`Step "${s.stepName || s.stepId}" is in ${c} screens (must be exactly 1)`);
    });
    car.screens.forEach((sc) => sc.stepIds.forEach((id) => {
      if (!stepIds.includes(id)) out.push(`Screen "${sc.screenName}" references unknown stepId "${id}"`);
    }));

    if (car.baseRenderOptionId) {
      const exists = car.steps.some((s) => s.options.some((o) => o.optionId === car.baseRenderOptionId));
      if (!exists) out.push(`baseRenderOptionId "${car.baseRenderOptionId}" not found in any step`);
    }

    const overlayIds = (car.overlays || []).map((o) => o.overlayId);
    const dupOverlay = overlayIds.find((id, i) => overlayIds.indexOf(id) !== i);
    if (dupOverlay) out.push(`Duplicate overlay id "${dupOverlay}"`);
    (car.overlays || []).forEach((ov) => {
      if (!ov.label.trim()) out.push(`Overlay "${ov.overlayId}" needs a button label`);
      if (ov.placement.position === 'afterStep' && !stepIds.includes(ov.placement.stepId || '')) {
        out.push(`Overlay "${ov.label || ov.overlayId}" must pick the step it sits above`);
      }
      if (!VIEW_KEYS.some((vk) => isRenderableImage(ov.images?.[vk]))) out.push(`Overlay "${ov.label || ov.overlayId}" has no images`);
    });

    const sceneryIds = (car.sceneries || []).map((s) => s.sceneryId);
    const dupScenery = sceneryIds.find((id, i) => sceneryIds.indexOf(id) !== i);
    if (dupScenery) out.push(`Duplicate scenery id "${dupScenery}"`);
    if ((car.sceneries || []).filter((s) => s.isDefault).length > 1) out.push('Only one scenery can be the default');
    (car.sceneries || []).forEach((sc) => { if (!sc.name.trim()) out.push(`Scenery "${sc.sceneryId}" needs a name`); });
    return out;
  }, [car]);

  // Blocking: every rendered view must be an image OR explicit Not-Apply.
  const imageIssues = useMemo((): string[] => {
    const out: string[] = [];
    car.steps.forEach((s) => {
      if (s.isTextOnly) return;
      s.options.forEach((o) => {
        if (!o.includedInBaseRender) {
          const miss = VIEW_KEYS.filter((vk) => !isViewResolved(o.images[vk]));
          if (miss.length) out.push(`${s.stepName || s.stepId} · ${o.optionName || o.optionId}: ${miss.length} view(s) without image or Not-Apply`);
        }
        o.subOptions?.forEach((sub) => {
          const miss = VIEW_KEYS.filter((vk) => !isViewResolved(sub.images[vk]));
          if (miss.length) out.push(`${s.stepName || s.stepId} · ${o.optionName || o.optionId} → ${sub.optionName || sub.optionId}: ${miss.length} view(s) without image or Not-Apply`);
        });
      });
    });
    return out;
  }, [car]);

  // Non-blocking warning: the same image URL used in more than one place.
  const duplicateImages = useMemo((): Array<{ url: string; locations: string[] }> => {
    const map = new Map<string, string[]>();
    const add = (url: string | undefined, loc: string) => {
      if (!isRenderableImage(url)) return;
      const arr = map.get(url) || [];
      arr.push(loc);
      map.set(url, arr);
    };
    car.steps.forEach((s) => s.options.forEach((o) => {
      VIEW_KEYS.forEach((vk) => add(o.images[vk], `${o.optionName || o.optionId} · ${vk}`));
      o.subOptions?.forEach((sub) => VIEW_KEYS.forEach((vk) =>
        add(sub.images[vk], `${o.optionName || o.optionId}→${sub.optionName || sub.optionId} · ${vk}`)));
    }));
    return [...map.entries()].filter(([, locs]) => locs.length > 1).map(([url, locations]) => ({ url, locations }));
  }, [car]);

  const canSubmit = validationErrors.length === 0 && imageIssues.length === 0;

  // Legacy cars leave views empty where a part isn't visible; the new model
  // wants that explicit. Fill only empty views (same rules as imageIssues).
  const markEmptyViewsNotApply = () => {
    const fill = (images: ViewImages): ViewImages => {
      const next = { ...images };
      VIEW_KEYS.forEach((vk) => { if (!isViewResolved(next[vk])) next[vk] = 'x'; });
      return next;
    };
    setSteps(car.steps.map((s) => (s.isTextOnly ? s : {
      ...s,
      options: s.options.map((o) => ({
        ...o,
        images: o.includedInBaseRender ? o.images : fill(o.images),
        subOptions: o.subOptions?.map((sub) => ({ ...sub, images: fill(sub.images) })),
      })),
    })));
  };

  /* ------------------------------------------------------------------ *
   * Step mutations.
   * ------------------------------------------------------------------ */
  const setSteps = (steps: Step[]) => setCar((c) => ({ ...c, steps }));

  const addStep = () => {
    const stepId = (() => {
      const base = slug('New step');
      let id = base;
      let i = 1;
      const ids = new Set(car.steps.map((s) => s.stepId));
      while (ids.has(id)) id = `${base}-${++i}`;
      return id;
    })();
    setExpandedSteps((prev) => new Set(prev).add(stepId));
    const newStep: Step = {
      stepId,
      stepName: 'New step',
      stepDescription: '',
      isTextOnly: false,
      preferredView: 'no_change',
      options: [],
    };
    setCar((c) => ({
      ...c,
      steps: [...c.steps, newStep],
      // Auto-place in its own screen to keep the "exactly one screen" invariant.
      screens: [
        ...c.screens,
        { screenId: stepId, screenName: newStep.stepName, order: c.screens.length + 1, stepIds: [stepId] },
      ],
    }));
  };

  const updateStep = (index: number, field: keyof Step, value: any) => {
    setSteps(car.steps.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  // Renaming a step re-derives its stepId (slug) and cascades the new id into
  // screens, rules and coupled sub-options so nothing dangles.
  const renameStep = (index: number, name: string) => {
    setCar((c) => {
      const oldId = c.steps[index].stepId;
      const base = slug(name) || 'step';
      const taken = new Set(c.steps.filter((_, i) => i !== index).map((s) => s.stepId));
      let newId = base;
      let k = 2;
      while (taken.has(newId)) newId = `${base}-${k++}`;
      if (newId === oldId) {
        return { ...c, steps: c.steps.map((s, i) => (i === index ? { ...s, stepName: name } : s)) };
      }
      const remap = (id: string) => (id === oldId ? newId : id);
      const steps = c.steps.map((s, i) => {
        const renamed = i === index ? { ...s, stepName: name, stepId: newId } : s;
        return {
          ...renamed,
          options: renamed.options.map((o) =>
            o.subOptionsLinkedToStepId === oldId ? { ...o, subOptionsLinkedToStepId: newId } : o
          ),
        };
      });
      const screens = c.screens.map((sc) => ({ ...sc, stepIds: sc.stepIds.map(remap) }));
      const rules = (c.rules || []).map((r) => ({
        ...r,
        when: { selections: r.when.selections.map((sel) => (sel.stepId === oldId ? { ...sel, stepId: newId } : sel)) },
        then: r.then.map((eff) =>
          'targetStepId' in eff && (eff as any).targetStepId === oldId ? { ...eff, targetStepId: newId } : eff
        ),
      }));
      const overlays = c.overlays?.map((ov) => ({
        ...ov,
        placement: ov.placement.stepId === oldId ? { ...ov.placement, stepId: newId } : ov.placement,
        hideStepIds: ov.hideStepIds?.map(remap),
      }));
      return { ...c, steps, screens, rules, overlays };
    });
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= car.steps.length) return;
    const steps = [...car.steps];
    [steps[index], steps[j]] = [steps[j], steps[index]];
    setSteps(steps);
  };
  const moveStepTo = (from: number, to: number) => setSteps(reorder(car.steps, from, to));

  const deleteStep = (index: number) => {
    const removed = car.steps[index];
    setCar((c) => ({
      ...c,
      steps: c.steps.filter((_, i) => i !== index),
      screens: c.screens.map((sc) => ({ ...sc, stepIds: sc.stepIds.filter((id) => id !== removed.stepId) })),
      overlays: c.overlays?.map((ov) => ({
        ...ov,
        placement: ov.placement.stepId === removed.stepId ? { position: 'top' as const } : ov.placement,
        hideStepIds: ov.hideStepIds?.filter((id) => id !== removed.stepId),
      })),
    }));
  };

  const addOption = (stepIndex: number) => {
    const step = car.steps[stepIndex];
    const newOption: Option = {
      optionId: genOptionId(step),
      optionName: '',
      optionDescription: '',
      optionPrice: 0,
      presentationImage: '',
      images: {},
      subOptions: [],
    };
    setSteps(car.steps.map((s, i) => (i === stepIndex ? { ...s, options: [...s.options, newOption] } : s)));
  };

  const updateOption = (stepIndex: number, optionIndex: number, field: keyof Option, value: any) => {
    setSteps(car.steps.map((s, i) => {
      if (i !== stepIndex) return s;
      const options = s.options.map((opt, oi) => {
        if (oi !== optionIndex) return field === 'isDefault' && value ? { ...opt, isDefault: false } : opt;
        return { ...opt, [field]: value };
      });
      return { ...s, options };
    }));
  };

  const deleteOption = (stepIndex: number, optionIndex: number) => {
    setSteps(car.steps.map((s, i) =>
      i === stepIndex ? { ...s, options: s.options.filter((_, oi) => oi !== optionIndex) } : s
    ));
  };

  const addSubOption = (stepIndex: number, optionIndex: number) => {
    setSteps(car.steps.map((s, i) => {
      if (i !== stepIndex) return s;
      return {
        ...s,
        options: s.options.map((opt, oi) => {
          if (oi !== optionIndex) return opt;
          const sub: SubOption = { optionId: genSubOptionId(opt), optionName: '', optionPrice: 0, images: {} };
          return { ...opt, subOptions: [...(opt.subOptions || []), sub] };
        }),
      };
    }));
  };

  const updateSubOption = (stepIndex: number, optionIndex: number, subIndex: number, field: keyof SubOption, value: any) => {
    setSteps(car.steps.map((s, i) => {
      if (i !== stepIndex) return s;
      return {
        ...s,
        options: s.options.map((opt, oi) => {
          if (oi !== optionIndex) return opt;
          const subOptions = (opt.subOptions || []).map((sub, si) => {
            if (si !== subIndex) return field === 'isDefault' && value ? { ...sub, isDefault: false } : sub;
            return { ...sub, [field]: value };
          });
          return { ...opt, subOptions };
        }),
      };
    }));
  };

  const deleteSubOption = (stepIndex: number, optionIndex: number, subIndex: number) => {
    setSteps(car.steps.map((s, i) => {
      if (i !== stepIndex) return s;
      return {
        ...s,
        options: s.options.map((opt, oi) =>
          oi === optionIndex ? { ...opt, subOptions: (opt.subOptions || []).filter((_, si) => si !== subIndex) } : opt
        ),
      };
    }));
  };

  /* ------------------------------------------------------------------ *
   * Image upload.
   * ------------------------------------------------------------------ */
  const handleImageUpload = async (file: File, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => {
    if (!file || !car.carName) { setError('Please enter car name before uploading images'); return; }
    const key = `${stepIndex}-${optionIndex}-${viewKey}-${subIndex ?? 'main'}`;
    setUploading((u) => ({ ...u, [key]: true }));
    setError(null);
    try {
      // @ts-ignore - uploadImage is JS without types
      const data = await uploadImage(file, car.carName || 'default');
      handleSetImageUrl(data.url, stepIndex, optionIndex, viewKey, subIndex);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const handlePresentationImageUpload = async (file: File, stepIndex: number, optionIndex: number) => {
    if (!file || !car.carName) { setError('Please enter car name before uploading images'); return; }
    const key = `${stepIndex}-${optionIndex}-presentation`;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      // @ts-ignore
      const data = await uploadImage(file, car.carName || 'default');
      updateOption(stepIndex, optionIndex, 'presentationImage', data.url);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  /** Upload for overlays / sceneries / splash: `apply` receives the stored URL. */
  const uploadAsset = async (file: File, key: string, apply: (url: string) => void) => {
    if (!file || !car.carName) { setError('Please enter car name before uploading images'); return; }
    setUploading((u) => ({ ...u, [key]: true }));
    setError(null);
    try {
      // @ts-ignore - uploadImage is JS without types
      const data = await uploadImage(file, car.carName || 'default');
      apply(data.url);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const handleSetImageUrl = (url: string, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => {
    if (subIndex !== undefined) {
      setSteps(car.steps.map((s, i) => i !== stepIndex ? s : {
        ...s,
        options: s.options.map((opt, oi) => oi !== optionIndex ? opt : {
          ...opt,
          subOptions: (opt.subOptions || []).map((sub, si) => si !== subIndex ? sub : { ...sub, images: { ...sub.images, [viewKey]: url } }),
        }),
      }));
    } else {
      setSteps(car.steps.map((s, i) => i !== stepIndex ? s : {
        ...s,
        options: s.options.map((opt, oi) => oi !== optionIndex ? opt : { ...opt, images: { ...opt.images, [viewKey]: url } }),
      }));
    }
  };

  /* ------------------------------------------------------------------ *
   * Screens.
   * ------------------------------------------------------------------ */
  const setScreens = (screens: Screen[]) => setCar((c) => ({ ...c, screens }));

  const addScreen = () => {
    const id = uid('screen');
    setScreens([...car.screens, { screenId: id, screenName: 'New screen', order: car.screens.length + 1, stepIds: [] }]);
  };
  const updateScreen = (index: number, field: keyof Screen, value: any) =>
    setScreens(car.screens.map((sc, i) => (i === index ? { ...sc, [field]: value } : sc)));
  const deleteScreen = (index: number) => setScreens(car.screens.filter((_, i) => i !== index));
  const moveScreen = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= car.screens.length) return;
    const screens = [...car.screens];
    [screens[index], screens[j]] = [screens[j], screens[index]];
    setScreens(screens.map((sc, i) => ({ ...sc, order: i + 1 })));
  };
  const moveScreenTo = (from: number, to: number) =>
    setScreens(reorder(car.screens, from, to).map((sc, i) => ({ ...sc, order: i + 1 })));
  const toggleScreenStep = (screenIndex: number, stepId: string) => {
    setScreens(car.screens.map((sc, i) => {
      if (i !== screenIndex) return sc;
      const has = sc.stepIds.includes(stepId);
      return { ...sc, stepIds: has ? sc.stepIds.filter((id) => id !== stepId) : [...sc.stepIds, stepId] };
    }));
  };
  const regenScreens = () => {
    askConfirm('Replace screens with one screen per step (in step order)?', () => {
      setScreens(car.steps.map((s, i) => ({ screenId: s.stepId, screenName: s.stepName, order: i + 1, stepIds: [s.stepId] })));
    }, { confirmLabel: 'Replace' });
  };

  /* ------------------------------------------------------------------ *
   * Rules.
   * ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ *
   * Overlays (frame, soft top, ...) and sceneries.
   * ------------------------------------------------------------------ */
  const setOverlays = (fn: (overlays: Overlay[]) => Overlay[]) => setCar((c) => ({ ...c, overlays: fn(c.overlays || []) }));
  const addOverlay = () => setOverlays((list) => [...list, {
    overlayId: uniqueSlug(`overlay ${list.length + 1}`, list.map((o) => o.overlayId), 'overlay'),
    label: '',
    images: {},
    placement: { position: 'top' },
    order: list.length,
  }]);
  const updateOverlay = (index: number, patch: Partial<Overlay>) =>
    setOverlays((list) => list.map((ov, i) => (i === index ? { ...ov, ...patch } : ov)));
  const moveOverlay = (index: number, dir: -1 | 1) =>
    setOverlays((list) => reorder(list, index, index + dir).map((ov, i) => ({ ...ov, order: i })));

  const setSceneries = (fn: (sceneries: Scenery[]) => Scenery[]) => setCar((c) => ({ ...c, sceneries: fn(c.sceneries || []) }));
  const addScenery = () => setSceneries((list) => [...list, {
    sceneryId: uniqueSlug(`scenery ${list.length + 1}`, list.map((s) => s.sceneryId), 'scenery'),
    name: '',
    images: {},
  }]);
  const updateScenery = (index: number, patch: Partial<Scenery>) =>
    setSceneries((list) => list.map((sc, i) => {
      if (i === index) return { ...sc, ...patch };
      return patch.isDefault ? { ...sc, isDefault: false } : sc;
    }));

  const requestDeleteOverlay = (index: number) =>
    askConfirm(`Delete overlay “${car.overlays?.[index]?.label || car.overlays?.[index]?.overlayId}”?`,
      () => setOverlays((list) => list.filter((_, i) => i !== index)), { confirmLabel: 'Delete', hold: true, danger: true });
  const requestDeleteScenery = (index: number) =>
    askConfirm(`Delete scenery “${car.sceneries?.[index]?.name || car.sceneries?.[index]?.sceneryId}”?`,
      () => setSceneries((list) => list.filter((_, i) => i !== index)), { confirmLabel: 'Delete', hold: true, danger: true });

  const setRules = (rules: Rule[]) => setCar((c) => ({ ...c, rules }));
  const addRule = () =>
    setRules([...(car.rules || []), { ruleId: uid('rule'), when: { selections: [] }, then: [] }]);
  const updateRule = (index: number, rule: Rule) =>
    setRules((car.rules || []).map((r, i) => (i === index ? rule : r)));
  const deleteRule = (index: number) => setRules((car.rules || []).filter((_, i) => i !== index));

  // Destructive deletes go through a hold-to-confirm modal.
  const HOLD_OPTS = { confirmLabel: 'Delete', hold: true, danger: true } as const;
  const requestDeleteStep = (index: number) =>
    askConfirm(`Delete step “${car.steps[index]?.stepName || car.steps[index]?.stepId}”? This can't be undone.`, () => deleteStep(index), HOLD_OPTS);
  const requestDeleteScreen = (index: number) =>
    askConfirm(`Delete screen “${car.screens[index]?.screenName || car.screens[index]?.screenId}”?`, () => deleteScreen(index), HOLD_OPTS);
  const requestDeleteRule = (index: number) =>
    askConfirm(`Delete rule “${(car.rules || [])[index]?.ruleId}”?`, () => deleteRule(index), HOLD_OPTS);

  /* ------------------------------------------------------------------ *
   * Load / save.
   * ------------------------------------------------------------------ */
  const openPicker = async () => {
    setShowPicker(true);
    setLoadingList(true);
    setError(null);
    try {
      const all = await fetchAllCars();
      setCars(all);
    } catch (err: any) {
      setError(err.message || 'Failed to load cars');
    } finally {
      setLoadingList(false);
    }
  };

  const openModels = async () => {
    setShowModels(true);
    setLoadingList(true);
    setError(null);
    try {
      setCars(await fetchAllCars());
    } catch (err: any) {
      setError(err.message || 'Failed to load cars');
    } finally {
      setLoadingList(false);
    }
  };

  // Known model names (suggestions for the Model field).
  useEffect(() => { fetchAllCars().then(setCars).catch(() => {}); }, []);
  const modelNames = useMemo(
    () => [...new Set(cars.map(modelNameOf).filter((n) => n !== UNASSIGNED))].sort(),
    [cars]
  );

  const loadCarForEdit = (raw: CarConfig) => {
    setCar(normalizeCar(raw));
    setLoadedFromLegacy(isLegacyCar(raw));
    setSavedMarker((m) => m + 1);
    setShowPicker(false);
    setShowModels(false);
    setSuccess(`Loaded "${raw.carName}" for editing.`);
    setError(null);
  };

  const loadExample = () => {
    const load = () => {
      setCar(normalizeCar({ ...exampleCar }));
      setSuccess('Example loaded (new format). Replace placeholder images with real URLs.');
      setError(null);
    };
    if (car.steps.length) askConfirm('Replace current form with the example car?', load, { confirmLabel: 'Replace' });
    else load();
  };

  const newCar = () => {
    const reset = () => { setCar(emptyCar()); setLoadedFromLegacy(false); setSuccess(null); setError(null); };
    if (!car.steps.length && !car.carName) { reset(); return; }
    askConfirm('Discard current form and start a new car?', reset, { confirmLabel: 'Discard' });
  };

  const buildPayload = () => ({
    carName: car.carName,
    basePrice: car.basePrice,
    baseRenderOptionId: car.baseRenderOptionId,
    steps: car.steps,
    screens: car.screens,
    rules: car.rules || [],
    isPublished: !!car.isPublished,
    displayOrder: car.displayOrder,
    splash: car.splash?.image || car.splash?.description?.trim()
      ? { image: car.splash.image || '', ...(car.splash.description?.trim() ? { description: car.splash.description.trim() } : {}) }
      : undefined,
    overlays: car.overlays || [],
    sceneries: car.sceneries || [],
    modelName: car.modelName?.trim() || undefined,
    modelId: car.modelName?.trim() ? slug(car.modelName) : undefined,
  });

  // Snapshot of the last loaded/saved state, to warn before publishing unsaved edits.
  useEffect(() => { setSavedJson(isEditing ? JSON.stringify(buildPayload()) : null); }, [savedMarker]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = isEditing && savedJson !== null && savedJson !== JSON.stringify(buildPayload());

  // Opens the save dialog (name can be changed; editing cars can also be copied).
  const handleSubmit = () => {
    if (!canSubmit) {
      const n = validationErrors.length + imageIssues.length;
      setError(`Resolve ${n} issue(s) before saving (missing images / Not-Apply or invalid config).`);
      return;
    }
    setSaveDialog({ name: car.carName });
  };

  const performSave = async (mode: 'save' | 'copy', name: string, versionNote = '') => {
    const carName = name.trim();
    if (!carName) { setError('Car name is required'); return; }
    setSaveDialog(null);
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...buildPayload(), carName, ...(versionNote.trim() ? { versionNote: versionNote.trim() } : {}) };
      if (mode === 'save' && isEditing) {
        const updated = await updateCar(car.id, car.createdAt, payload);
        setCar(normalizeCar(updated));
        setLoadedFromLegacy(false);
        setSavedMarker((m) => m + 1);
        setHistoryKey((k) => k + 1);
        setSuccess(`Saved. Updated at ${updated.updatedAt || 'now'}.`);
      } else {
        // New car, or "Save as copy" (also creates a new Webflow CMS item).
        // Copies start unpublished so they never show up in the splash by accident.
        const result = await createCar(mode === 'copy' ? { ...payload, isPublished: false } : payload);
        const created = result.car || result;
        if (created?.id) setCar(normalizeCar(created));
        setLoadedFromLegacy(false);
        setSavedMarker((m) => m + 1);
        setHistoryKey((k) => k + 1);
        setSuccess(mode === 'copy' ? `Saved as a new car "${carName}" (${created?.id || ''}).` : `Created car ${created?.id || ''}.`);
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to save car configuration.');
    } finally {
      setSubmitting(false);
    }
  };

  // Every render of the car (current form state, saved or not) as a ZIP,
  // one folder per view → per step. See carWizard/downloadRenders.ts.
  const downloadRenders = async () => {
    if (zipProgress) return;
    const total = collectRenderEntries(car).length;
    if (!total) { setError('This car has no uploaded renders to download yet.'); return; }
    setError(null);
    setSuccess(null);
    setZipProgress({ done: 0, total });
    try {
      const res = await downloadRendersZip(car, (done, t) => setZipProgress({ done, total: t }));
      if (res.failed.length) {
        setError(`${res.fileName}: ${res.failed.length} of ${res.total} image(s) could not be downloaded (listed in missing.txt).`);
      } else {
        setSuccess(`Downloaded ${res.downloaded} render(s) → ${res.fileName}`);
      }
    } catch (err: any) {
      console.error('Renders ZIP error:', err);
      setError(err.message || 'Failed to build the renders ZIP.');
    } finally {
      setZipProgress(null);
    }
  };

  const exportJSON = () => {
    const dataStr = JSON.stringify(buildPayload(), null, 2);
    const a = document.createElement('a');
    a.setAttribute('href', 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr));
    a.setAttribute('download', `${car.carName || 'car-config'}.json`);
    a.click();
  };

  const importJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target?.result as string);
          setCar(normalizeCar({ ...emptyCar(), ...imported }));
          setSuccess('Configuration imported.');
        } catch {
          setError('Failed to import JSON.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /* ================================================================== */
  const tabs = [
    ['steps', 'Steps', car.steps.length],
    ['screens', 'Screens', car.screens.length],
    ['rules', 'Rules', car.rules?.length || 0],
    ['overlays', 'Overlays', car.overlays?.length || 0],
    ['sceneries', 'Sceneries', car.sceneries?.length || 0],
    ...(isEditing ? [['history', 'History', ''] as const] : []),
  ] as const;

  // A history version goes into the form; it only becomes current on Save.
  const restoreVersion = (snapshot: CarConfig, meta: { versionAt: string }) => {
    setCar(normalizeCar({ ...snapshot, id: car.id, createdAt: car.createdAt }));
    setSuccess(`Loaded the version from ${new Date(meta.versionAt).toLocaleString()}. Save to make it the current one.`);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {showPicker && (
        <CarPicker cars={cars} loading={loadingList} onPick={loadCarForEdit} onClose={() => setShowPicker(false)} />
      )}

      {saveDialog && (
        <SaveDialog
          initialName={saveDialog.name}
          isEditing={isEditing}
          onSave={(name, note) => performSave('save', name, note)}
          onCopy={(name, note) => performSave('copy', name, note)}
          onCancel={() => setSaveDialog(null)}
        />
      )}

      {showModels && (
        <ModelsManager cars={cars} loading={loadingList} staging={IS_PREVIEW} onOpen={loadCarForEdit} onClose={() => setShowModels(false)} />
      )}

      {showPublish && (
        <PublishDialog
          car={car}
          dirty={dirty}
          onCancel={() => setShowPublish(false)}
          onDone={(msg) => {
            setShowPublish(false);
            setSuccess(msg);
            setError(null);
            setHistoryKey((k) => k + 1);
            setCar((c) => ({ ...c, lastPublishedAt: new Date().toISOString() }));
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          hold={confirmState.hold}
          danger={confirmState.danger}
          onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* Sticky toolbar */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center gap-3">
          <Car size={22} className="text-indigo-600" />
          <h1 className="text-lg font-bold tracking-tight whitespace-nowrap">Configurator Admin</h1>
          {isEditing ? (
            <span className="adm-chip bg-amber-100 text-amber-800" title={car.id}><Pencil size={11} /> Editing · PATCH</span>
          ) : (
            <span className="adm-chip bg-emerald-100 text-emerald-700"><Plus size={11} /> New car</span>
          )}

          <div className="flex-1" />

          <button onClick={newCar} className="adm-btn adm-btn-ghost px-3 py-2 text-sm">New</button>
          <button onClick={openModels} className="adm-btn adm-btn-ghost px-3 py-2 text-sm" title="Cars grouped by model"><Boxes size={15} /> Models</button>
          <button onClick={openPicker} className="adm-btn adm-btn-ghost px-3 py-2 text-sm">Load…</button>
          <button onClick={loadExample} className="adm-btn adm-btn-ghost px-3 py-2 text-sm"><Sparkles size={15} /> Example</button>
          <button onClick={importJSON} className="adm-btn adm-btn-ghost px-3 py-2 text-sm"><Upload size={15} /> Import</button>
          <button onClick={exportJSON} className="adm-btn adm-btn-ghost px-3 py-2 text-sm"><Download size={15} /> Export</button>
          <button
            onClick={downloadRenders}
            disabled={!!zipProgress}
            title="Download every render as a ZIP, organised by view and step"
            className="adm-btn adm-btn-ghost px-3 py-2 text-sm"
          >
            {zipProgress
              ? <><Loader2 size={15} className="animate-spin" /> {zipProgress.done}/{zipProgress.total}</>
              : <><FileArchive size={15} /> Renders ZIP</>}
          </button>
          <button onClick={() => setShowPreview((p) => !p)} className="adm-btn adm-btn-subtle px-3 py-2 text-sm">
            {showPreview ? <><EyeOff size={15} /> Hide preview</> : <><Eye size={15} /> Preview</>}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            title={canSubmit ? (isEditing ? 'Save changes via PATCH' : 'Create the car') : `${validationErrors.length + imageIssues.length} issue(s) to resolve before saving`}
            className={`adm-btn px-4 py-2 text-sm ${isEditing ? 'adm-btn-primary' : 'adm-btn-success'}`}
          >
            {submitting ? (
              <><Loader2 size={15} className="animate-spin" /> Saving…</>
            ) : !canSubmit ? (
              <><AlertTriangle size={15} /> {validationErrors.length + imageIssues.length} issue{validationErrors.length + imageIssues.length === 1 ? '' : 's'}</>
            ) : isEditing ? <><Save size={15} /> Save…</> : <><Save size={15} /> Create car…</>}
          </button>
          {IS_PREVIEW && isEditing && (
            <button
              onClick={() => setShowPublish(true)}
              title="Copy the saved version of this car and its images to the live site"
              className="adm-btn adm-btn-success px-4 py-2 text-sm"
            >
              <Rocket size={15} /> Publish to production
            </button>
          )}
        </div>
      </header>

      {/* Toasts */}
      <div className="fixed top-20 right-5 z-40 space-y-2 w-80 pointer-events-none">
        {error && (
          <div className="adm-toast pointer-events-auto bg-rose-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="opacity-80 hover:opacity-100"><X size={16} /></button>
          </div>
        )}
        {success && (
          <div className="adm-toast pointer-events-auto bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" /><span className="flex-1">{success}</span>
            <button onClick={() => setSuccess(null)} className="opacity-80 hover:opacity-100"><X size={16} /></button>
          </div>
        )}
      </div>

      <div className={`max-w-[1500px] mx-auto px-5 py-6 ${showPreview ? 'flex gap-6 items-start' : ''}`}>
        <div className={showPreview ? 'flex-1 min-w-0' : ''}>
          {validationErrors.length > 0 && (
            <div className="adm-fade bg-rose-50 border border-rose-200 text-rose-900 px-4 py-3 rounded-xl mb-4">
              <div className="font-semibold mb-1 text-sm flex items-center gap-1.5"><AlertTriangle size={15} /> {validationErrors.length} configuration issue(s)</div>
              <ul className="list-disc pl-5 text-sm space-y-0.5">
                {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {imageIssues.length > 0 && (
            <div className="adm-fade bg-rose-50 border border-rose-200 text-rose-900 px-4 py-3 rounded-xl mb-4">
              <div className="font-semibold mb-1 text-sm flex items-center gap-1.5"><AlertTriangle size={15} /> {imageIssues.length} option(s) with unresolved views</div>
              <p className="text-xs mb-1 flex items-center gap-3">
                <span>Every view must have an image or be marked “Not Apply”. Submit is blocked until resolved.</span>
                <button
                  onClick={() => askConfirm('Mark every empty view as “Not Apply”? Views that already have an image are left alone.', markEmptyViewsNotApply, { confirmLabel: 'Mark Not Apply' })}
                  className="adm-btn adm-btn-subtle px-2.5 py-1 text-xs whitespace-nowrap"
                >Mark all empty as Not Apply</button>
              </p>
              <ul className="list-disc pl-5 text-sm space-y-0.5 max-h-40 overflow-y-auto">
                {imageIssues.slice(0, 12).map((e, i) => <li key={i}>{e}</li>)}
                {imageIssues.length > 12 && <li className="italic">+ {imageIssues.length - 12} more…</li>}
              </ul>
            </div>
          )}

          {duplicateImages.length > 0 && (
            <div className="adm-fade bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-xl mb-4">
              <div className="font-semibold mb-1 text-sm flex items-center gap-1.5"><AlertTriangle size={15} /> {duplicateImages.length} image(s) reused in multiple places</div>
              <p className="text-xs mb-1">Same URL used more than once — verify it is intentional (won’t block saving).</p>
              <ul className="list-disc pl-5 text-xs space-y-0.5 max-h-40 overflow-y-auto">
                {duplicateImages.slice(0, 10).map((d, i) => (
                  <li key={i}><span className="font-mono">…{d.url.slice(-28)}</span> → {d.locations.join(', ')}</li>
                ))}
                {duplicateImages.length > 10 && <li className="italic">+ {duplicateImages.length - 10} more…</li>}
              </ul>
            </div>
          )}

          {loadedFromLegacy && (
            <div className="adm-fade bg-sky-50 border border-sky-200 text-sky-900 px-4 py-3 rounded-xl mb-4 text-sm">
              <div className="font-semibold mb-1 flex items-center gap-1.5"><Sparkles size={15} /> Legacy car converted to the new format</div>
              Screens, sub-option coupling and rules (incl. Contrast package → black steering wheel) were derived from the
              legacy setup. Check the live preview, then <b>Save</b> to migrate this car. Until you save, production keeps
              using the legacy configurator.
            </div>
          )}

          {/* Basic Info */}
          <div className="adm-card p-5 mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-4">Basic Information</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3 grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" title="Family this car belongs to (e.g. Supercabrio). Groups cars in Models.">Model</label>
                  <input type="text" list="admin-model-names" value={car.modelName || ''}
                    onChange={(e) => setCar({ ...car, modelName: e.target.value })}
                    className="adm-input px-3 py-2 text-sm" placeholder="e.g., Supercabrio" />
                  <datalist id="admin-model-names">{modelNames.map((n) => <option key={n} value={n} />)}</datalist>
                </div>
                {IS_PREVIEW && isEditing && (
                  <div className="col-span-2 flex items-end pb-2 text-xs text-slate-500">
                    {car.lastPublishedAt
                      ? <>Last published to production: <b className="ml-1">{new Date(car.lastPublishedAt).toLocaleString()}</b></>
                      : <>Not published to production from staging yet.</>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Car Name</label>
                <input type="text" value={car.carName} onChange={(e) => setCar({ ...car, carName: e.target.value })}
                  className="adm-input px-3 py-2 text-sm" placeholder="e.g., Mercedes G-Wagon" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Base Price ($)</label>
                <input type="number" value={car.basePrice} onChange={(e) => setCar({ ...car, basePrice: Number(e.target.value) })}
                  className="adm-input px-3 py-2 text-sm" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Base Render Option</label>
                <select value={car.baseRenderOptionId || ''} onChange={(e) => setCar({ ...car, baseRenderOptionId: e.target.value })}
                  className="adm-input px-3 py-2 text-sm">
                  <option value="">Select base render option…</option>
                  {car.steps.flatMap((s) => s.options.map((o) => (
                    <option key={o.optionId} value={o.optionId}>{s.stepName}: {o.optionName} ({o.optionId})</option>
                  )))}
                </select>
              </div>
            </div>

            {/* Showroom (model splash) */}
            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 items-start">
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" title="Show this car in the “Select model” splash">
                  <input type="checkbox" checked={!!car.isPublished}
                    onChange={(e) => setCar({ ...car, isPublished: e.target.checked })} className="w-4 h-4 rounded" />
                  Show in model picker
                </label>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Picker order</label>
                  <input type="number" value={car.displayOrder ?? ''} placeholder="1"
                    onChange={(e) => setCar({ ...car, displayOrder: e.target.value === '' ? undefined : Number(e.target.value) })}
                    className="adm-input px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Picker image (square) — the card shows the Model name below it (or the car name if no model is set)</label>
                <SingleImageSlot
                  url={car.splash?.image}
                  uploading={!!uploading['splash']}
                  onUpload={(f) => uploadAsset(f, 'splash', (url) => setCar((c) => ({ ...c, splash: { ...c.splash, image: url } })))}
                  onClear={() => setCar((c) => ({ ...c, splash: c.splash?.description ? { ...c.splash, image: '' } : undefined }))}
                  square
                />
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 mt-3">Picker description — short text under the name (optional; press Enter for a new line)</label>
                <textarea
                  rows={3}
                  maxLength={240}
                  value={car.splash?.description || ''}
                  onChange={(e) => setCar((c) => ({ ...c, splash: { image: c.splash?.image || '', description: e.target.value } }))}
                  placeholder="e.g., Open-top G-Wagon built for coastal roads."
                  className="adm-input px-3 py-2 text-sm resize-none"
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="adm-tabs mb-5">
            {tabs.map(([key, label, count]) => (
              <button key={key} onClick={() => setActiveTab(key)} className={`adm-tab ${activeTab === key ? 'active' : ''}`}>
                {label} <span className="opacity-60">{count}</span>
              </button>
            ))}
          </div>

          {/* Steps tab */}
          {activeTab === 'steps' && (
            <div key="steps" className="adm-fade mb-8">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slate-500 max-w-xl">
                  Each step is one question; the Step ID auto-derives from the name.
                  <span className="block text-xs text-slate-400 mt-0.5">
                    Order = render order. The base render sits at the back; steps <b>lower in this list draw on top</b> (front layer). Drag the handle (or use ↑/↓) to restack.
                  </span>
                </p>
                <button onClick={addStep} className="adm-btn adm-btn-primary px-4 py-2 text-sm"><Plus size={16} /> Add Step</button>
              </div>
              {car.steps.length === 0 && (
                <div className="border-2 border-dashed border-slate-200 rounded-xl py-12 text-center text-slate-400">
                  <Layers size={32} className="mx-auto mb-2 opacity-60" />
                  No steps yet — add one, load the example, or import a JSON.
                </div>
              )}

              {car.steps.map((step, stepIndex) => {
                const open = expandedSteps.has(step.stepId);
                return (
                  <div
                    key={step.stepId}
                    className={`adm-card mb-3 overflow-hidden ${dragStep === stepIndex ? 'opacity-50' : ''}`}
                    onDragOver={(e) => { if (dragStep !== null) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (dragStep !== null) moveStepTo(dragStep, stepIndex); setDragStep(null); }}
                  >
                    {/* Header row (always visible) */}
                    <div className={`flex items-center gap-3 p-3 ${open ? 'border-b border-slate-100' : ''}`}>
                      <span
                        title="Drag to reorder"
                        draggable
                        onDragStart={() => setDragStep(stepIndex)}
                        onDragEnd={() => setDragStep(null)}
                        className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
                      ><GripVertical size={16} /></span>
                      <button title={open ? 'Collapse' : 'Expand'} onClick={() => toggleStepExpanded(step.stepId)}
                        className={`text-slate-400 hover:text-slate-700 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}><ChevronRight size={18} /></button>
                      <input type="text" value={step.stepName} title="Step name — the Step ID is auto-derived from it"
                        onChange={(e) => renameStep(stepIndex, e.target.value)}
                        className="adm-input flex-1 px-2.5 py-1.5 text-sm font-medium" placeholder="Step name (e.g. Engine)" />
                      <span title="Auto-generated stable id (used by screens & rules)" className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded-md">{step.stepId}</span>
                      <span className="text-xs text-slate-400 whitespace-nowrap" title="Number of options">{step.options.length} opt</span>
                      {step.isTextOnly && <span className="adm-chip bg-slate-100 text-slate-500" title="Text-only step (no images)"><Type size={11} /> txt</span>}
                      <button title="Move up" onClick={() => moveStep(stepIndex, -1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronUp size={15} /></button>
                      <button title="Move down" onClick={() => moveStep(stepIndex, 1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronDown size={15} /></button>
                      <button title="Delete step" onClick={() => requestDeleteStep(stepIndex)} className="adm-btn adm-btn-danger px-2 py-1.5 text-sm"><Trash2 size={15} /></button>
                    </div>

                    {/* Body (animated accordion) */}
                    <div className={`adm-acc ${open ? 'open' : ''}`}>
                      <div className="adm-acc-inner">
                        <div className="px-4 pb-4 pt-3">
                          <div className="grid grid-cols-3 gap-4 mb-3">
                            <div>
                              <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                              <input type="text" value={step.stepDescription}
                                onChange={(e) => updateStep(stepIndex, 'stepDescription', e.target.value)}
                                className="adm-input px-3 py-2 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-slate-500 mb-1">Preferred View</label>
                              <select value={step.preferredView || 'no_change'}
                                onChange={(e) => updateStep(stepIndex, 'preferredView', e.target.value as PreferredView)}
                                className="adm-input px-3 py-2 text-sm">
                                <option value="no_change">No change</option>
                                {VIEW_LABELS.map((l, i) => <option key={i} value={`view${i + 1}`}>{l}</option>)}
                              </select>
                            </div>
                            <div className="flex items-end gap-5 pb-1">
                              <label className="flex items-center cursor-pointer" title="No images/views — only affects price (e.g. Engine)">
                                <input type="checkbox" checked={step.isTextOnly || false}
                                  onChange={(e) => updateStep(stepIndex, 'isTextOnly', e.target.checked)} className="w-4 h-4 rounded" />
                                <span className="ml-2 text-sm text-slate-600 inline-flex items-center gap-1"><Type size={14} /> Text only</span>
                              </label>
                              <label className="flex items-center cursor-pointer" title="User must pick an option before continuing">
                                <input type="checkbox" checked={step.required || false}
                                  onChange={(e) => updateStep(stepIndex, 'required', e.target.checked)} className="w-4 h-4 rounded" />
                                <span className="ml-2 text-sm text-slate-600">Required</span>
                              </label>
                            </div>
                          </div>

                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Options</h3>
                            <button onClick={() => addOption(stepIndex)} className="adm-btn adm-btn-success px-3 py-1.5 text-sm"><Plus size={15} /> Add Option</button>
                          </div>
                          {step.options.length === 0 && <p className="text-sm text-slate-400 italic mb-2">No options yet.</p>}
                          {step.options.map((option, optionIndex) => (
                            <OptionEditor
                              key={option.optionId}
                              option={option}
                              stepIndex={stepIndex}
                              optionIndex={optionIndex}
                              isTextOnly={step.isTextOnly || false}
                              allSteps={car.steps}
                              onUpdate={updateOption}
                              onDelete={deleteOption}
                              onAddSubOption={addSubOption}
                              onUpdateSubOption={updateSubOption}
                              onDeleteSubOption={deleteSubOption}
                              onImageUpload={handleImageUpload}
                              onPresentationImageUpload={handlePresentationImageUpload}
                              onSetImageUrl={handleSetImageUrl}
                              uploading={uploading}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Screens tab */}
          {activeTab === 'screens' && (
            <div key="screens" className="adm-fade">
              <ScreensEditor
                screens={car.screens}
                steps={car.steps}
                onAdd={addScreen}
                onUpdate={updateScreen}
                onDelete={requestDeleteScreen}
                onMove={moveScreen}
                onReorder={moveScreenTo}
                onToggleStep={toggleScreenStep}
                onRegen={regenScreens}
              />
            </div>
          )}

          {/* Rules tab */}
          {activeTab === 'rules' && (
            <div key="rules" className="adm-fade">
              <RulesEditor
                rules={car.rules || []}
                steps={car.steps}
                screens={car.screens}
                onAdd={addRule}
                onUpdate={updateRule}
                onDelete={requestDeleteRule}
              />
            </div>
          )}

          {/* Overlays tab */}
          {activeTab === 'overlays' && (
            <div key="overlays" className="adm-fade">
              <OverlaysEditor
                overlays={car.overlays || []}
                steps={car.steps}
                baseStepId={car.steps.find((s) => s.options.some((o) => o.optionId === car.baseRenderOptionId))?.stepId || null}
                uploading={uploading}
                onAdd={addOverlay}
                onUpdate={updateOverlay}
                onMove={moveOverlay}
                onDelete={requestDeleteOverlay}
                onUpload={(index, viewKey, file) => uploadAsset(file, `overlay-${index}-${viewKey}`, (url) =>
                  setOverlays((list) => list.map((ov, i) => (i === index ? { ...ov, images: { ...ov.images, [viewKey]: url } } : ov))))}
              />
            </div>
          )}

          {/* History tab */}
          {activeTab === 'history' && isEditing && (
            <div key="history" className="adm-fade">
              <HistoryPanel car={car} refreshKey={historyKey} onRestore={restoreVersion} />
            </div>
          )}

          {/* Sceneries tab */}
          {activeTab === 'sceneries' && (
            <div key="sceneries" className="adm-fade">
              <SceneriesEditor
                sceneries={car.sceneries || []}
                uploading={uploading}
                onAdd={addScenery}
                onUpdate={updateScenery}
                onDelete={requestDeleteScenery}
                onUpload={(index, field, viewKey, file) => uploadAsset(file, `scenery-${index}-${field}-${viewKey ?? ''}`, (url) =>
                  setSceneries((list) => list.map((sc, i) => {
                    if (i !== index) return sc;
                    if (field === 'thumbnail') return { ...sc, thumbnail: url };
                    return { ...sc, [field]: { ...(sc[field] || {}), [viewKey!]: url } };
                  })))}
              />
            </div>
          )}
        </div>

        {showPreview && (
          <div className="w-[420px] shrink-0 sticky top-20 self-start">
            <div className="adm-card p-2">
              <div className="text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide px-1 flex items-center justify-between">
                <span>Live Preview</span><span className="text-[10px] font-normal text-slate-400">mobile</span>
              </div>
              <div className="bg-white rounded-lg overflow-hidden mx-auto border border-slate-100" style={{ width: '390px', height: 'calc(100vh - 130px)' }}>
                <CarWizardConfiguratorNew carData={previewCarData} embedded />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Confirm modal (replaces native confirm)
 * ==================================================================== */
const HOLD_MS = 1800;

/** Press-and-hold button: fills over HOLD_MS; releasing early cancels. */
const HoldButton: React.FC<{ label: string; onComplete: () => void }> = ({ label, onComplete }) => {
  const [holding, setHolding] = useState(false);
  const timer = React.useRef<number | undefined>(undefined);

  const start = () => {
    setHolding(true);
    timer.current = window.setTimeout(() => { setHolding(false); onComplete(); }, HOLD_MS);
  };
  const cancel = () => {
    setHolding(false);
    if (timer.current) window.clearTimeout(timer.current);
  };
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  return (
    <button
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      className="relative overflow-hidden adm-btn adm-btn-danger px-4 py-2 text-sm select-none touch-none"
    >
      <span
        className="absolute inset-y-0 left-0 bg-rose-600/40"
        style={{ width: holding ? '100%' : '0%', transition: `width ${holding ? HOLD_MS : 150}ms linear` }}
      />
      <span className="relative flex items-center gap-1.5">
        <Trash2 size={15} /> {holding ? 'Keep holding…' : label}
      </span>
    </button>
  );
};

const ConfirmModal: React.FC<{
  message: string;
  confirmLabel?: string;
  hold?: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ message, confirmLabel = 'Confirm', hold, danger, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onCancel}>
    <div className="adm-pop bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-3 mb-5">
        <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${danger ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
          <AlertTriangle size={20} />
        </div>
        <p className="text-sm text-slate-700 pt-1.5">{message}</p>
      </div>
      <div className="flex justify-end items-center gap-2">
        <button onClick={onCancel} className="adm-btn adm-btn-ghost px-4 py-2 text-sm" autoFocus>Cancel</button>
        {hold ? (
          <HoldButton label={confirmLabel} onComplete={onConfirm} />
        ) : (
          <button onClick={onConfirm} className={`adm-btn px-4 py-2 text-sm ${danger ? 'adm-btn-danger' : 'adm-btn-primary'}`}>{confirmLabel}</button>
        )}
      </div>
      {hold && <p className="text-[11px] text-slate-400 mt-3 text-right">Press and hold to confirm</p>}
    </div>
  </div>
);

/* ==================================================================== *
 * Car picker
 * ==================================================================== */
const CarPicker: React.FC<{
  cars: CarConfig[];
  loading: boolean;
  onPick: (car: CarConfig) => void;
  onClose: () => void;
}> = ({ cars, loading, onPick, onClose }) => {
  const sortedCars = useMemo(() => {
    const time = (v?: string) => {
      const t = v ? Date.parse(v) : NaN;
      return Number.isNaN(t) ? -Infinity : t;
    };
    return [...cars].sort((a, b) => time(b.createdAt) - time(a.createdAt));
  }, [cars]);

  return (
  <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="adm-pop bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Load existing car</h2>
        <button onClick={onClose} className="adm-btn adm-btn-subtle px-3 py-1.5 text-sm">Close</button>
      </div>
      {loading ? (
        <p className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : cars.length === 0 ? (
        <p className="text-slate-500">No cars found.</p>
      ) : (
        <div className="space-y-2">
          {sortedCars.map((c) => (
            <button key={`${c.id}-${c.createdAt}`} onClick={() => onPick(c)}
              className="adm-card w-full text-left p-3 hover:border-indigo-300">
              <div className="font-medium">{c.carName || '(unnamed)'}</div>
              <div className="text-xs text-slate-400 font-mono">{c.id} · {c.createdAt}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.steps?.length || 0} steps · {c.screens?.length || 0} screens · {c.rules?.length || 0} rules</div>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};

/* ==================================================================== *
 * Searchable step picker for a screen (pills + search dropdown)
 * ==================================================================== */
const ScreenStepPicker: React.FC<{
  screenIndex: number;
  screen: Screen;
  steps: Step[];
  assignedElsewhere: (screenIndex: number, stepId: string) => boolean;
  onToggle: (screenIndex: number, stepId: string) => void;
}> = ({ screenIndex, screen, steps, assignedElsewhere, onToggle }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = screen.stepIds
    .map((id) => steps.find((s) => s.stepId === id))
    .filter((s): s is Step => !!s);

  const candidates = steps.filter(
    (s) =>
      !screen.stepIds.includes(s.stepId) &&
      !assignedElsewhere(screenIndex, s.stepId) &&
      (s.stepName || s.stepId).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      {/* Selected pills */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.length === 0 && <span className="text-xs text-slate-400 italic">No steps in this screen yet.</span>}
        {selected.map((s) => (
          <span key={s.stepId} className="adm-pop inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">
            {s.stepName || s.stepId}
            <button onClick={() => onToggle(screenIndex, s.stepId)} className="hover:text-indigo-900"><X size={12} /></button>
          </span>
        ))}
      </div>

      {/* Search box + dropdown */}
      <div className="relative max-w-xs">
        <div className="flex items-center gap-2 adm-input px-2.5 py-1.5">
          <Search size={14} className="text-slate-400 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            placeholder="Add a step…"
            className="w-full text-sm outline-none bg-transparent"
          />
        </div>
        {open && (
          <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg adm-pop">
            {candidates.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No unassigned steps match.</div>
            ) : (
              candidates.map((s) => (
                <button
                  key={s.stepId}
                  onMouseDown={(e) => { e.preventDefault(); onToggle(screenIndex, s.stepId); setQuery(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between"
                >
                  <span>{s.stepName || s.stepId}</span>
                  <span className="text-xs font-mono text-slate-400">{s.stepId}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Screens editor
 * ==================================================================== */
const ScreensEditor: React.FC<{
  screens: Screen[];
  steps: Step[];
  onAdd: () => void;
  onUpdate: (index: number, field: keyof Screen, value: any) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onReorder: (from: number, to: number) => void;
  onToggleStep: (screenIndex: number, stepId: string) => void;
  onRegen: () => void;
}> = ({ screens, steps, onAdd, onUpdate, onDelete, onMove, onReorder, onToggleStep, onRegen }) => {
  const assignedElsewhere = (screenIndex: number, stepId: string) =>
    screens.some((sc, i) => i !== screenIndex && sc.stepIds.includes(stepId));
  const [drag, setDrag] = useState<number | null>(null);

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Group steps into wizard screens and set their order. Each step lives in exactly one screen.</p>
        <div className="flex gap-2">
          <button onClick={onRegen} className="adm-btn adm-btn-subtle px-3 py-2 text-sm"><RotateCcw size={14} /> 1 per step</button>
          <button onClick={onAdd} className="adm-btn adm-btn-primary px-4 py-2 text-sm"><Plus size={16} /> Add Screen</button>
        </div>
      </div>
      {screens.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400">No screens — add one or reset to 1-per-step.</div>
      )}
      <div className="space-y-3">
        {screens.map((sc, i) => (
          <div
            key={sc.screenId}
            className={`adm-card p-3 ${drag === i ? 'opacity-50' : ''}`}
            onDragOver={(e) => { if (drag !== null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (drag !== null) onReorder(drag, i); setDrag(null); }}
          >
            <div className="flex items-center gap-2 mb-2.5">
              <span
                title="Drag to reorder"
                draggable
                onDragStart={() => setDrag(i)}
                onDragEnd={() => setDrag(null)}
                className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
              ><GripVertical size={16} /></span>
              <span className="text-xs font-bold text-slate-400 w-8 text-center bg-slate-100 rounded py-1">{sc.order}</span>
              <input value={sc.screenName} onChange={(e) => onUpdate(i, 'screenName', e.target.value)}
                className="adm-input flex-1 px-2.5 py-1.5 text-sm font-medium" placeholder="Screen name" />
              <input value={sc.screenDescription || ''} onChange={(e) => onUpdate(i, 'screenDescription', e.target.value)}
                className="adm-input flex-1 px-2.5 py-1.5 text-sm" placeholder="Description (optional)" />
              <button onClick={() => onMove(i, -1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronUp size={15} /></button>
              <button onClick={() => onMove(i, 1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronDown size={15} /></button>
              <button onClick={() => onDelete(i)} className="adm-btn adm-btn-danger px-2 py-1.5 text-sm"><Trash2 size={15} /></button>
            </div>
            <ScreenStepPicker screenIndex={i} screen={sc} steps={steps} assignedElsewhere={assignedElsewhere} onToggle={onToggleStep} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Rules editor
 * ==================================================================== */
const RulesEditor: React.FC<{
  rules: Rule[];
  steps: Step[];
  screens: Screen[];
  onAdd: () => void;
  onUpdate: (index: number, rule: Rule) => void;
  onDelete: (index: number) => void;
}> = ({ rules, steps, screens, onAdd, onUpdate, onDelete }) => {
  const stepById = (id: string) => steps.find((s) => s.stepId === id);

  const needsOption = (t: RuleEffectType) => t === 'disableOption' || t === 'hideOption' || t === 'forceOption' || t === 'forceSubOption';
  const needsStep = (t: RuleEffectType) => needsOption(t) || t === 'disableStep' || t === 'hideStep';

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">
          Conditional logic: WHEN selections match, THEN hide / disable / force options, steps or screens.
          <span className="block text-xs text-slate-400 mt-0.5"><b>forceSubOption</b> locks a variant while that option is picked (e.g. Contrast package → Original steering wheel in black).</span>
        </p>
        <button onClick={onAdd} className="adm-btn adm-btn-primary px-4 py-2 text-sm"><Plus size={16} /> Add Rule</button>
      </div>
      {rules.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400">No rules yet.</div>
      )}
      <div className="space-y-4">
        {rules.map((rule, ri) => (
          <div key={rule.ruleId} className="adm-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-slate-400">ruleId</span>
              <input value={rule.ruleId} onChange={(e) => onUpdate(ri, { ...rule, ruleId: e.target.value })}
                className="adm-input flex-1 px-2.5 py-1.5 text-sm font-mono" />
              <button onClick={() => onDelete(ri)} className="adm-btn adm-btn-danger px-3 py-1.5 text-sm"><Trash2 size={14} /> Delete</button>
            </div>

            {/* WHEN */}
            <div className="mb-3 rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="adm-chip bg-sky-100 text-sky-700">WHEN · all match</span>
                <button
                  onClick={() => onUpdate(ri, { ...rule, when: { selections: [...rule.when.selections, { stepId: '', optionIdIn: [] }] } })}
                  className="adm-btn adm-btn-subtle px-2.5 py-1 text-xs"><Plus size={13} /> condition</button>
              </div>
              {rule.when.selections.map((sel, si) => {
                const step = stepById(sel.stepId);
                return (
                  <div key={si} className="flex items-center gap-2 mb-1">
                    <select value={sel.stepId}
                      onChange={(e) => {
                        const selections = rule.when.selections.map((x, i) => i === si ? { stepId: e.target.value, optionIdIn: [] } : x);
                        onUpdate(ri, { ...rule, when: { selections } });
                      }}
                      className="px-2 py-1 border rounded text-sm">
                      <option value="">step…</option>
                      {steps.map((s) => <option key={s.stepId} value={s.stepId}>{s.stepName || s.stepId}</option>)}
                    </select>
                    <span className="text-xs text-gray-500">in</span>
                    <select multiple value={sel.optionIdIn}
                      onChange={(e) => {
                        const optionIdIn = Array.from(e.target.selectedOptions).map((o) => o.value);
                        const selections = rule.when.selections.map((x, i) => i === si ? { ...x, optionIdIn } : x);
                        onUpdate(ri, { ...rule, when: { selections } });
                      }}
                      className="px-2 py-1 border rounded text-sm min-w-[160px] h-16">
                      {step?.options.map((o) => <option key={o.optionId} value={o.optionId}>{o.optionName} ({o.optionId})</option>)}
                    </select>
                    <button
                      onClick={() => onUpdate(ri, { ...rule, when: { selections: rule.when.selections.filter((_, i) => i !== si) } })}
                      className="adm-btn adm-btn-danger px-2 py-1.5 text-xs"><X size={13} /></button>
                  </div>
                );
              })}
            </div>

            {/* THEN */}
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="adm-chip bg-violet-100 text-violet-700">THEN</span>
                <button
                  onClick={() => onUpdate(ri, { ...rule, then: [...rule.then, { type: 'hideStep', targetStepId: '' } as RuleEffect] })}
                  className="adm-btn adm-btn-subtle px-2.5 py-1 text-xs"><Plus size={13} /> effect</button>
              </div>
              {rule.then.map((eff, ei) => {
                const targetStepId = (eff as any).targetStepId || '';
                const step = stepById(targetStepId);
                const setEff = (next: RuleEffect) =>
                  onUpdate(ri, { ...rule, then: rule.then.map((x, i) => i === ei ? next : x) });
                return (
                  <div key={ei} className="flex items-center gap-2 mb-1 flex-wrap">
                    <select value={eff.type}
                      onChange={(e) => {
                        const type = e.target.value as RuleEffectType;
                        let next: RuleEffect;
                        if (type === 'hideScreen') next = { type, targetScreenId: '' };
                        else if (type === 'forceSubOption') next = { type, targetStepId: '', targetOptionId: '', targetSubOptionId: '' };
                        else if (needsOption(type)) next = { type, targetStepId: '', targetOptionId: '' } as RuleEffect;
                        else next = { type, targetStepId: '' } as RuleEffect;
                        setEff(next);
                      }}
                      className="px-2 py-1 border rounded text-sm">
                      {EFFECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {eff.type === 'hideScreen' ? (
                      <select value={eff.targetScreenId}
                        onChange={(e) => setEff({ type: 'hideScreen', targetScreenId: e.target.value })}
                        className="px-2 py-1 border rounded text-sm">
                        <option value="">screen…</option>
                        {screens.map((sc) => <option key={sc.screenId} value={sc.screenId}>{sc.screenName}</option>)}
                      </select>
                    ) : (
                      <>
                        {needsStep(eff.type) && (
                          <select value={targetStepId}
                            onChange={(e) => setEff({ ...(eff as any), targetStepId: e.target.value, ...(needsOption(eff.type) ? { targetOptionId: '' } : {}) })}
                            className="px-2 py-1 border rounded text-sm">
                            <option value="">step…</option>
                            {steps.map((s) => <option key={s.stepId} value={s.stepId}>{s.stepName || s.stepId}</option>)}
                          </select>
                        )}
                        {needsOption(eff.type) && (
                          <select value={(eff as any).targetOptionId || ''}
                            onChange={(e) => setEff({ ...(eff as any), targetOptionId: e.target.value, ...(eff.type === 'forceSubOption' ? { targetSubOptionId: '' } : {}) })}
                            className="px-2 py-1 border rounded text-sm">
                            <option value="">option…</option>
                            {(eff.type === 'forceSubOption' ? step?.options.filter((o) => o.subOptions?.length) : step?.options)
                              ?.map((o) => <option key={o.optionId} value={o.optionId}>{o.optionName} ({o.optionId})</option>)}
                          </select>
                        )}
                        {eff.type === 'forceSubOption' && (
                          <select value={eff.targetSubOptionId || ''}
                            onChange={(e) => setEff({ ...eff, targetSubOptionId: e.target.value })}
                            className="px-2 py-1 border rounded text-sm">
                            <option value="">variant…</option>
                            {step?.options.find((o) => o.optionId === eff.targetOptionId)?.subOptions
                              ?.map((s) => <option key={s.optionId} value={s.optionId}>{s.optionName} ({s.optionId})</option>)}
                          </select>
                        )}
                      </>
                    )}
                    <button onClick={() => onUpdate(ri, { ...rule, then: rule.then.filter((_, i) => i !== ei) })}
                      className="adm-btn adm-btn-danger px-2 py-1.5 text-xs"><X size={13} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Option editor
 * ==================================================================== */
interface OptionEditorProps {
  option: Option;
  stepIndex: number;
  optionIndex: number;
  isTextOnly: boolean;
  allSteps: Step[];
  onUpdate: (stepIndex: number, optionIndex: number, field: keyof Option, value: any) => void;
  onDelete: (stepIndex: number, optionIndex: number) => void;
  onAddSubOption: (stepIndex: number, optionIndex: number) => void;
  onUpdateSubOption: (stepIndex: number, optionIndex: number, subIndex: number, field: keyof SubOption, value: any) => void;
  onDeleteSubOption: (stepIndex: number, optionIndex: number, subIndex: number) => void;
  onPresentationImageUpload: (file: File, stepIndex: number, optionIndex: number) => void;
  onImageUpload: (file: File, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => void;
  onSetImageUrl: (url: string, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => void;
  uploading: { [key: string]: boolean };
}

const OptionEditor: React.FC<OptionEditorProps> = ({
  option, stepIndex, optionIndex, isTextOnly, allSteps,
  onUpdate, onDelete, onAddSubOption, onUpdateSubOption, onDeleteSubOption,
  onPresentationImageUpload, onImageUpload, onSetImageUrl, uploading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const linkedStep = allSteps.find((s) => s.stepId === option.subOptionsLinkedToStepId);
  const linkedOptions = linkedStep?.options || [];

  return (
    <div className="adm-card mb-3 p-4 bg-slate-50/60">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="grid grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Option ID</label>
              <input type="text" value={option.optionId}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'optionId', e.target.value)}
                className="adm-input px-2.5 py-1.5 text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Option Name</label>
              <input type="text" value={option.optionName}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'optionName', e.target.value)}
                className="adm-input px-2.5 py-1.5 text-sm" placeholder="5.0L V8" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Price ($)</label>
              <input type="number" value={option.optionPrice}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'optionPrice', Number(e.target.value))}
                className="adm-input px-2.5 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
              <input type="text" value={option.optionDescription || ''}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'optionDescription', e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm" />
            </div>
          </div>

          {!isTextOnly && (
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium mb-1">Presentation Image</label>
                <input type="file" accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onPresentationImageUpload(f, stepIndex, optionIndex); }}
                  className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              {option.presentationImage && (
                <div className="w-20 h-12 border rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img src={option.presentationImage} alt="Presentation" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
          )}

          <div className="space-y-2 mt-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" title="Pre-selected by default in the wizard">
              <input type="checkbox" checked={option.isDefault || false}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'isDefault', e.target.checked)} className="w-4 h-4 rounded" />
              Default option
            </label>
            {!isTextOnly && (
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" title="Already part of the base render — no per-view images needed">
                <input type="checkbox" checked={option.includedInBaseRender || false}
                  onChange={(e) => onUpdate(stepIndex, optionIndex, 'includedInBaseRender', e.target.checked)} className="w-4 h-4 rounded" />
                <Check size={15} className="text-emerald-600" /> Included in Base Render
              </label>
            )}
            <div title="Couple this option's sub-options to another step (e.g. steering follows interior color)">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Couple sub-options to step (linked)</label>
              <select value={option.subOptionsLinkedToStepId || ''}
                onChange={(e) => onUpdate(stepIndex, optionIndex, 'subOptionsLinkedToStepId', e.target.value || undefined)}
                className="adm-input px-2.5 py-1.5 text-sm">
                <option value="">— not coupled —</option>
                {allSteps.filter((s) => s.stepId !== allSteps[stepIndex]?.stepId).map((s) => (
                  <option key={s.stepId} value={s.stepId}>{s.stepName || s.stepId}</option>
                ))}
              </select>
              {option.subOptionsLinkedToStepId && (
                <p className="text-xs text-slate-400 mt-1">Each sub-option resolves by its “linked option”; leave a sub unlinked to make it a fixed manual variant.</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 ml-3">
          {!isTextOnly && !option.includedInBaseRender && (
            <button onClick={() => setExpanded(!expanded)} className="adm-btn adm-btn-subtle px-2.5 py-1.5 text-sm">
              <ChevronRight size={15} className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} /> Views
            </button>
          )}
          <button onClick={() => onDelete(stepIndex, optionIndex)} className="adm-btn adm-btn-danger px-2.5 py-1.5 text-sm"><Trash2 size={15} /></button>
        </div>
      </div>

      {expanded && !isTextOnly && !option.includedInBaseRender && (
        <div className="adm-fade">
          <ViewImagesEditor images={option.images} stepIndex={stepIndex} optionIndex={optionIndex}
            onImageUpload={onImageUpload} onSetImageUrl={onSetImageUrl} uploading={uploading} />

          <div className="mt-4 pl-4 border-l-2 border-indigo-200">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Sub-Options (variants)</h4>
              <button onClick={() => onAddSubOption(stepIndex, optionIndex)} className="adm-btn adm-btn-subtle px-2.5 py-1.5 text-xs"><Plus size={13} /> Add Sub-Option</button>
            </div>
            {option.subOptions?.map((sub, subIndex) => (
              <div key={sub.optionId} className="mb-3 p-3 rounded border bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">Name</label>
                      <input type="text" value={sub.optionName}
                        onChange={(e) => onUpdateSubOption(stepIndex, optionIndex, subIndex, 'optionName', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm" placeholder="Off white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Sub-Option ID</label>
                      <input type="text" value={sub.optionId}
                        onChange={(e) => onUpdateSubOption(stepIndex, optionIndex, subIndex, 'optionId', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Price ($)</label>
                      <input type="number" value={sub.optionPrice}
                        onChange={(e) => onUpdateSubOption(stepIndex, optionIndex, subIndex, 'optionPrice', Number(e.target.value))}
                        className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                    {option.subOptionsLinkedToStepId && (
                      <div>
                        <label className="block text-xs font-medium mb-1">Linked option</label>
                        <select value={sub.linkedOptionId || ''}
                          onChange={(e) => onUpdateSubOption(stepIndex, optionIndex, subIndex, 'linkedOptionId', e.target.value || undefined)}
                          className="w-full px-2 py-1 border rounded text-sm">
                          <option value="">— fixed (manual) —</option>
                          {linkedOptions.map((o) => <option key={o.optionId} value={o.optionId}>{o.optionName} ({o.optionId})</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <button onClick={() => onDeleteSubOption(stepIndex, optionIndex, subIndex)} className="adm-btn adm-btn-danger ml-2 px-2 py-1.5 text-xs"><Trash2 size={13} /></button>
                </div>
                <label className="flex items-center gap-2 mb-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={sub.isDefault || false}
                    onChange={(e) => onUpdateSubOption(stepIndex, optionIndex, subIndex, 'isDefault', e.target.checked)} className="w-4 h-4 rounded" />
                  Default variant
                </label>
                <ViewImagesEditor images={sub.images} stepIndex={stepIndex} optionIndex={optionIndex} subOptionIndex={subIndex}
                  onImageUpload={onImageUpload} onSetImageUrl={onSetImageUrl} uploading={uploading} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================================================================== *
 * View images editor
 * ==================================================================== */
interface ViewImagesEditorProps {
  images: ViewImages;
  stepIndex: number;
  optionIndex: number;
  subOptionIndex?: number;
  onImageUpload: (file: File, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => void;
  onSetImageUrl: (url: string, stepIndex: number, optionIndex: number, viewKey: string, subIndex?: number) => void;
  uploading: { [key: string]: boolean };
}

const ViewImagesEditor: React.FC<ViewImagesEditorProps> = ({
  images, stepIndex, optionIndex, subOptionIndex, onImageUpload, onSetImageUrl, uploading,
}) => (
  <div className="grid grid-cols-3 gap-3 mt-3">
    {VIEW_LABELS.map((label, idx) => {
      const viewKey = `view${idx + 1}` as ViewKey;
      const uploadKey = `${stepIndex}-${optionIndex}-${viewKey}-${subOptionIndex ?? 'main'}`;
      const imageUrl = images[viewKey];
      const hasImage = imageUrl && imageUrl.startsWith('http');
      const isNotApply = imageUrl === 'x' || imageUrl === 'not visible in this view';
      return (
        <div key={viewKey} className="border rounded p-3 bg-gray-50"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onImageUpload(f, stepIndex, optionIndex, viewKey, subOptionIndex); }}>
          <label className="block text-xs font-semibold mb-2">{label}</label>
          {hasImage ? (
            <div className="mb-2">
              <img src={imageUrl} alt={label} className="w-full h-24 object-cover rounded border" />
              <button title="Remove this image" onClick={() => onSetImageUrl('', stepIndex, optionIndex, viewKey, subOptionIndex)} className="mt-1 w-full px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">Remove</button>
            </div>
          ) : isNotApply ? (
            <div className="mb-2 p-2 bg-gray-200 rounded text-center" title="Marked as not applicable for this view">
              <p className="text-xs text-gray-600">Not Apply</p>
              <button title="Clear marker" onClick={() => onSetImageUrl('', stepIndex, optionIndex, viewKey, subOptionIndex)} className="mt-1 w-full px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400">Clear</button>
            </div>
          ) : (
            <div className="mb-2 p-2 bg-rose-50 rounded border-2 border-dashed border-rose-300 text-center" title="Unresolved — upload an image or mark Not Apply (blocks saving)">
              <p className="text-xs text-rose-500 font-medium">Needs image / Not Apply</p>
            </div>
          )}
          {!isNotApply && (
            <label className="block">
              <input type="file" accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onImageUpload(f, stepIndex, optionIndex, viewKey, subOptionIndex); }}
                className="hidden" disabled={uploading[uploadKey]} />
              <div title="Upload an image for this view" className={`w-full px-3 py-2 text-center rounded text-xs font-medium cursor-pointer inline-flex items-center justify-center gap-1.5 ${uploading[uploadKey] ? 'bg-blue-300 text-blue-800 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                {uploading[uploadKey] ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : <><Upload size={13} /> {hasImage ? 'Replace' : 'Upload'}</>}
              </div>
            </label>
          )}
          {!hasImage && !isNotApply && (
            <button onClick={() => onSetImageUrl('x', stepIndex, optionIndex, viewKey, subOptionIndex)} className="adm-btn adm-btn-subtle mt-2 w-full px-2 py-1.5 text-xs"><X size={13} /> Not Apply</button>
          )}
        </div>
      );
    })}
  </div>
);

/* ==================================================================== *
 * Save dialog (rename / save as copy)
 * ==================================================================== */
const SaveDialog: React.FC<{
  initialName: string;
  isEditing: boolean;
  onSave: (name: string, note: string) => void;
  onCopy: (name: string, note: string) => void;
  onCancel: () => void;
}> = ({ initialName, isEditing, onSave, onCopy, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [note, setNote] = useState('');
  const valid = name.trim().length > 0;
  const renamed = isEditing && name.trim() !== initialName.trim();

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onCancel}>
      <form
        className="adm-pop bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (valid) onSave(name, note); }}
      >
        <h2 className="text-lg font-bold mb-1">{isEditing ? 'Save configuration' : 'Create car'}</h2>
        <p className="text-sm text-slate-500 mb-4">
          {isEditing
            ? 'Rename it if you like. “Save as copy” creates a separate car (and a new Webflow item) and leaves this one untouched.'
            : 'Choose the name for the new car.'}
        </p>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">Name</label>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="adm-input px-3 py-2 text-sm mb-1" />
        {renamed && <p className="text-xs text-amber-700 mb-1">Will rename “{initialName}” → “{name.trim()}”.</p>}
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 mt-3">Note for the history (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed?" className="adm-input px-3 py-2 text-sm" />
        <div className="flex justify-end items-center gap-2 mt-5">
          <button type="button" onClick={onCancel} className="adm-btn adm-btn-ghost px-4 py-2 text-sm">Cancel</button>
          {isEditing && (
            <button type="button" disabled={!valid} onClick={() => onCopy(name, note)} className="adm-btn adm-btn-subtle px-4 py-2 text-sm">
              <Copy size={15} /> Save as copy
            </button>
          )}
          <button type="submit" disabled={!valid} className={`adm-btn px-4 py-2 text-sm ${isEditing ? 'adm-btn-primary' : 'adm-btn-success'}`}>
            <Save size={15} /> {isEditing ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ==================================================================== *
 * Image slots for overlays / sceneries / splash
 * ==================================================================== */
const SingleImageSlot: React.FC<{
  url?: string;
  uploading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
  square?: boolean;
  label?: string;
}> = ({ url, uploading, onUpload, onClear, square, label }) => {
  const has = isRenderableImage(url);
  return (
    <div
      className="border rounded p-3 bg-gray-50"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
    >
      {label && <label className="block text-xs font-semibold mb-2">{label}</label>}
      <div className="flex items-center gap-3">
        <div className={`${square ? 'w-24 h-24' : 'w-full h-24'} shrink-0 rounded border bg-white overflow-hidden flex items-center justify-center`}
          style={has ? undefined : { backgroundImage: 'repeating-conic-gradient(#e2e8f0 0% 25%, transparent 0% 50%)', backgroundSize: '12px 12px' }}>
          {has ? <img src={url} alt="" className={`w-full h-full ${square ? 'object-cover' : 'object-contain'}`} /> : <span className="text-[11px] text-slate-400">No image</span>}
        </div>
        <div className="flex flex-col gap-1.5 min-w-[110px]">
          <label className="block">
            <input type="file" accept="image/*" className="hidden" disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.currentTarget.value = ''; }} />
            <div className={`w-full px-3 py-2 text-center rounded text-xs font-medium cursor-pointer inline-flex items-center justify-center gap-1.5 ${uploading ? 'bg-blue-300 text-blue-800 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {uploading ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : <><Upload size={13} /> {has ? 'Replace' : 'Upload'}</>}
            </div>
          </label>
          {has && (
            <button type="button" onClick={onClear} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">Remove</button>
          )}
        </div>
      </div>
    </div>
  );
};

/** One slot per view. Views without an image simply don't show the layer. */
const ViewImageSlots: React.FC<{
  images: ViewImages;
  uploadingKey: (viewKey: ViewKey) => string;
  uploading: { [key: string]: boolean };
  onUpload: (viewKey: ViewKey, file: File) => void;
  onClear: (viewKey: ViewKey) => void;
}> = ({ images, uploadingKey, uploading, onUpload, onClear }) => (
  <div className="grid grid-cols-3 gap-3 mt-3">
    {VIEW_LABELS.map((label, idx) => {
      const viewKey = `view${idx + 1}` as ViewKey;
      return (
        <SingleImageSlot
          key={viewKey}
          label={label}
          url={images?.[viewKey]}
          uploading={!!uploading[uploadingKey(viewKey)]}
          onUpload={(f) => onUpload(viewKey, f)}
          onClear={() => onClear(viewKey)}
        />
      );
    })}
  </div>
);

/* ==================================================================== *
 * Overlays editor (frame, soft top, ...)
 * ==================================================================== */
const OverlaysEditor: React.FC<{
  overlays: Overlay[];
  steps: Step[];
  baseStepId: string | null;
  uploading: { [key: string]: boolean };
  onAdd: () => void;
  onUpdate: (index: number, patch: Partial<Overlay>) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onDelete: (index: number) => void;
  onUpload: (index: number, viewKey: ViewKey, file: File) => void;
}> = ({ overlays, steps, baseStepId, uploading, onAdd, onUpdate, onMove, onDelete, onUpload }) => {
  // Placement select value: "top" | "bottom" | "after:<stepId>".
  const placementValue = (ov: Overlay) =>
    ov.placement.position === 'afterStep' ? `after:${ov.placement.stepId || ''}` : ov.placement.position;
  const setPlacement = (index: number, value: string) => {
    const [position, stepId] = value.startsWith('after:') ? ['afterStep', value.slice(6)] : [value, undefined];
    onUpdate(index, { placement: { position: position as OverlayPosition, ...(stepId ? { stepId } : {}) } });
  };
  const groups = Array.from(new Set(overlays.map((o) => o.exclusiveGroup?.trim()).filter((g): g is string => !!g)));

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500 max-w-xl">
          Toggleable layers shown as buttons under the car (e.g. “show frame”, “show soft top”). They never change the price.
          <span className="block text-xs text-slate-400 mt-0.5">
            Upload one transparent render per view; views without an image hide the button there. <b>Layer</b> decides where it sits in the stack.
          </span>
        </p>
        <button onClick={onAdd} className="adm-btn adm-btn-primary px-4 py-2 text-sm"><Plus size={16} /> Add Overlay</button>
      </div>
      {overlays.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400">
          <ToggleRight size={30} className="mx-auto mb-2 opacity-60" />
          No overlays yet.
        </div>
      )}
      <datalist id="overlay-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
      <div className="space-y-4">
        {overlays.map((ov, i) => (
          <div key={ov.overlayId} className="adm-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded-md" title="Stable id (saved in shared builds)">{ov.overlayId}</span>
              <div className="flex-1" />
              <button title="Move left in the button row" onClick={() => onMove(i, -1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronUp size={15} /></button>
              <button title="Move right in the button row" onClick={() => onMove(i, 1)} className="adm-btn adm-btn-subtle px-2 py-1.5 text-sm"><ChevronDown size={15} /></button>
              <button title="Delete overlay" onClick={() => onDelete(i)} className="adm-btn adm-btn-danger px-2 py-1.5 text-sm"><Trash2 size={15} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Button label</label>
                <input value={ov.label} placeholder="show frame" onChange={(e) => onUpdate(i, { label: e.target.value })} className="adm-input px-2.5 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Label when on (optional)</label>
                <input value={ov.labelActive || ''} placeholder="hide frame" onChange={(e) => onUpdate(i, { labelActive: e.target.value || undefined })} className="adm-input px-2.5 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Layer</label>
                <select value={placementValue(ov)} onChange={(e) => setPlacement(i, e.target.value)} className="adm-input px-2.5 py-1.5 text-sm">
                  <option value="top">On top of everything</option>
                  <option value="bottom">Just above the base render</option>
                  <optgroup label="Right above step…">
                    {steps.filter((s) => s.stepId !== baseStepId && !s.isTextOnly).map((s) => (
                      <option key={s.stepId} value={`after:${s.stepId}`}>{s.stepName || s.stepId}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Hide these layers while on</label>
                <select multiple value={ov.hideStepIds || []}
                  onChange={(e) => onUpdate(i, { hideStepIds: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                  className="adm-input px-2 py-1 text-sm h-20">
                  {steps.filter((s) => !s.isTextOnly).map((s) => <option key={s.stepId} value={s.stepId}>{s.stepName || s.stepId}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1" title="Overlays with the same group can't be on at the same time">Exclusive group (optional)</label>
                <input list="overlay-groups" value={ov.exclusiveGroup || ''} placeholder="roof"
                  onChange={(e) => onUpdate(i, { exclusiveGroup: e.target.value || undefined })} className="adm-input px-2.5 py-1.5 text-sm" />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!ov.defaultOn} onChange={(e) => onUpdate(i, { defaultOn: e.target.checked })} className="w-4 h-4 rounded" />
                  On by default
                </label>
              </div>
            </div>
            <ViewImageSlots
              images={ov.images}
              uploading={uploading}
              uploadingKey={(vk) => `overlay-${i}-${vk}`}
              onUpload={(vk, f) => onUpload(i, vk, f)}
              onClear={(vk) => onUpdate(i, { images: { ...ov.images, [vk]: '' } })}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Sceneries editor (backgrounds behind the car)
 * ==================================================================== */
const SceneriesEditor: React.FC<{
  sceneries: Scenery[];
  uploading: { [key: string]: boolean };
  onAdd: () => void;
  onUpdate: (index: number, patch: Partial<Scenery>) => void;
  onDelete: (index: number) => void;
  onUpload: (index: number, field: 'thumbnail' | 'images' | 'shadowImages', viewKey: ViewKey | null, file: File) => void;
}> = ({ sceneries, uploading, onAdd, onUpdate, onDelete, onUpload }) => {
  const [shadowOpen, setShadowOpen] = useState<Set<string>>(new Set());
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500 max-w-xl">
          Backgrounds shown behind the car (lake, desert, forest…). Car renders must have a transparent background.
          <span className="block text-xs text-slate-400 mt-0.5">
            Use the same size / aspect ratio as the car renders so they line up. Without a default scenery the configurator
            starts on the white studio (offered as “Studio” in the picker).
          </span>
        </p>
        <button onClick={onAdd} className="adm-btn adm-btn-primary px-4 py-2 text-sm"><Plus size={16} /> Add Scenery</button>
      </div>
      {sceneries.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400">
          <Mountain size={30} className="mx-auto mb-2 opacity-60" />
          No sceneries yet — the car shows on the white studio.
        </div>
      )}
      <div className="space-y-4">
        {sceneries.map((sc, i) => (
          <div key={sc.sceneryId} className="adm-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded-md">{sc.sceneryId}</span>
              <div className="flex-1" />
              <button title="Delete scenery" onClick={() => onDelete(i)} className="adm-btn adm-btn-danger px-2 py-1.5 text-sm"><Trash2 size={15} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-1 items-start">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
                  <input value={sc.name} placeholder="Lake" onChange={(e) => onUpdate(i, { name: e.target.value })} className="adm-input px-2.5 py-1.5 text-sm" />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!sc.isDefault} onChange={(e) => onUpdate(i, { isDefault: e.target.checked })} className="w-4 h-4 rounded" />
                  Default scenery
                </label>
              </div>
              <div className="col-span-2">
                <SingleImageSlot
                  label="Picker thumbnail (round)"
                  url={sc.thumbnail}
                  square
                  uploading={!!uploading[`scenery-${i}-thumbnail-`]}
                  onUpload={(f) => onUpload(i, 'thumbnail', null, f)}
                  onClear={() => onUpdate(i, { thumbnail: undefined })}
                />
              </div>
            </div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-4">Background per view</h4>
            <ViewImageSlots
              images={sc.images}
              uploading={uploading}
              uploadingKey={(vk) => `scenery-${i}-images-${vk}`}
              onUpload={(vk, f) => onUpload(i, 'images', vk, f)}
              onClear={(vk) => onUpdate(i, { images: { ...sc.images, [vk]: '' } })}
            />
            <button
              type="button"
              onClick={() => setShadowOpen((prev) => { const n = new Set(prev); n.has(sc.sceneryId) ? n.delete(sc.sceneryId) : n.add(sc.sceneryId); return n; })}
              className="adm-btn adm-btn-subtle px-2.5 py-1.5 text-xs mt-3"
            >
              <ChevronRight size={13} className={`transition-transform duration-200 ${shadowOpen.has(sc.sceneryId) ? 'rotate-90' : ''}`} /> Car shadow per view (optional)
            </button>
            {shadowOpen.has(sc.sceneryId) && (
              <ViewImageSlots
                images={sc.shadowImages || {}}
                uploading={uploading}
                uploadingKey={(vk) => `scenery-${i}-shadowImages-${vk}`}
                onUpload={(vk, f) => onUpload(i, 'shadowImages', vk, f)}
                onClear={(vk) => onUpdate(i, { shadowImages: { ...(sc.shadowImages || {}), [vk]: '' } })}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CarAdminFormNew;
